/**
 * Phase 8b — One Thing Mode
 * Phase 8d — Hardening pass:
 *   - readiness_degrading & duplicate_attention_chaos suppressed from PRIMARY
 *     (still visible in "Show all") via PredictedFailure.suppressedFromPrimary.
 *   - followup_dropping_ball converted to inline Mark Done flow that clears
 *     the actual overdue follow-ups via setFollowUpTasks.
 *   - Route-based modes use a soft handoff: Maverick stays mounted, button
 *     reads "Open in [Module]" instead of "Send & Close Loop", and navigation
 *     does NOT auto-close the loop or exit Maverick. The loop only closes
 *     when the operator explicitly returns and confirms.
 *
 * Maverick is a Loop Closure Engine, not a workspace. Four states:
 *   1. WATCHING   — engine quiet
 *   2. PREDICTING — one item with failure mode, evidence, intervention
 *   3. CLOSING    — operator clicks intervention; brief inline execution
 *   4. CLOSED     — 1.5s confirmation, then next item or back to WATCHING
 */

import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, X, Eye, CheckCircle2, ChevronRight, Edit3, Clock, ExternalLink } from "lucide-react";
import { useMaverick } from "@/data/maverick/context";
import { useAppData } from "@/data/AppDataContext";
import { runPreMortem, type PredictedFailure } from "@/data/maverick/preMortemEngine";
import {
  resolveMaverickIntervention,
  type MaverickInterventionPlan,
} from "@/data/maverick/interventionResolver";
import { runAction } from "@/lib/actionRunner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { OrderingModal } from "@/components/OrderingModal";
import { QuickAssignModal } from "@/components/quick-actions/QuickAssignModal";
import { QuickResolveIssueModal } from "@/components/quick-actions/QuickResolveIssueModal";
import { LogInteractionModal } from "@/components/quick-actions/LogInteractionModal";
import { MaverickHowItWorksDrawer } from "@/components/maverick/MaverickHowItWorksDrawer";
import { MaverickTestPanel, isMaverickDebugEnabled } from "@/components/maverick/MaverickTestPanel";
import { toast } from "sonner";
import type { Job, Issue } from "@/data/types";
import type { FollowUpTask } from "@/data/opportunityTypes";

type LoopState = "watching" | "predicting" | "closing" | "closed";

type ActiveModal =
  | { kind: "ordering"; job: Job; openIssues?: Issue[] }
  | { kind: "quickAssign"; job: Job; openIssues?: Issue[] }
  | { kind: "quickResolveIssue"; issue: Issue }
  | { kind: "logInteraction"; customerName: string; relatedJobId?: string; defaultNote?: string }
  | { kind: "markFollowUpsDone"; job: Job }
  | null;

/** Friendly module label per route — used by the soft-handoff CTA. */
function routeToModuleLabel(route?: string): string {
  if (!route) return "module";
  if (route.startsWith("/warehouse")) return "Warehouse";
  if (route.startsWith("/permitting")) return "Permitting";
  if (route.startsWith("/accounting")) return "Accounting";
  if (route.startsWith("/follow-ups")) return "Follow-ups";
  if (route.startsWith("/install-readiness")) return "Readiness";
  if (route.startsWith("/scheduling")) return "Scheduling";
  if (route.startsWith("/issues")) return "Issues";
  if (route.startsWith("/jobs/")) return "Job Detail";
  return "module";
}

export function MaverickLayout() {
  const { disableMaverick, setHandoff } = useMaverick();
  const { jobs, issues, followUpTasks, setFollowUpTasks, buildReviewEvents, salesOpportunities } = useAppData();
  const navigate = useNavigate();

  const result = useMemo(
    () => runPreMortem({ jobs, issues, followUpTasks, buildReviewEvents, opportunities: salesOpportunities }),
    [jobs, issues, followUpTasks, buildReviewEvents, salesOpportunities],
  );

  const [closedJobIds, setClosedJobIds] = useState<Set<string>>(new Set());
  const [state, setState] = useState<LoopState>("watching");
  const [showAll, setShowAll] = useState(false);
  const [closingJobId, setClosingJobId] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  /** All not-yet-closed predictions, in confidence order. Used by "Show all". */
  const allRemaining = useMemo(
    () => result.predictions.filter((p) => !closedJobIds.has(p.jobId)),
    [result.predictions, closedJobIds],
  );

  /** The PRIMARY queue — excludes suppressed-from-primary modes. This is what
   *  drives the One Thing card. Suppressed items still appear in `allRemaining`
   *  so the operator can find them via "Show all". */
  const primaryQueue = useMemo(
    () => allRemaining.filter((p) => !p.suppressedFromPrimary),
    [allRemaining],
  );

  const current: PredictedFailure | null = primaryQueue[0] ?? null;
  /** "N more" counter spans the FULL remaining list (incl. suppressed) so
   *  the operator sees true backlog depth without being interrupted. */
  const moreAfter = Math.max(0, allRemaining.length - (current ? 1 : 0));

  // Auto-transition between WATCHING and PREDICTING based on data.
  useEffect(() => {
    if (state === "closing" || state === "closed") return;
    setState(current ? "predicting" : "watching");
  }, [current, state]);

  const advanceAfterClose = useCallback((jobId: string) => {
    setClosingJobId(jobId);
    setState("closed");
    setTimeout(() => {
      setClosedJobIds((prev) => new Set(prev).add(jobId));
      setClosingJobId(null);
      setState("predicting");
    }, 1500);
  }, []);

  const handleCloseLoop = () => {
    if (!current) return;
    advanceAfterClose(current.jobId);
  };

  /** Primary "act" path. Resolves the plan and either opens a modal inline
   *  or performs a soft handoff (navigate, but keep Maverick mounted). */
  const handleAct = () => {
    if (!current) return;
    const plan: MaverickInterventionPlan = resolveMaverickIntervention(current, { jobs, issues });

    if (plan.mode === "modal" && plan.modal && plan.payload) {
      switch (plan.modal) {
        case "ordering":
          if (plan.payload.job) {
            setActiveModal({
              kind: "ordering",
              job: plan.payload.job,
              openIssues: plan.payload.openIssues,
            });
            return;
          }
          break;
        case "quickAssign":
          if (plan.payload.job) {
            setActiveModal({
              kind: "quickAssign",
              job: plan.payload.job,
              openIssues: plan.payload.openIssues,
            });
            return;
          }
          break;
        case "quickResolveIssue":
          if (plan.payload.issue) {
            setActiveModal({ kind: "quickResolveIssue", issue: plan.payload.issue });
            return;
          }
          break;
        case "logInteraction":
          if (plan.payload.customerName) {
            setActiveModal({
              kind: "logInteraction",
              customerName: plan.payload.customerName,
              relatedJobId: plan.payload.relatedJobId,
              defaultNote: plan.payload.defaultNote,
            });
            return;
          }
          break;
        case "markFollowUpsDone":
          if (plan.payload.job) {
            setActiveModal({ kind: "markFollowUpsDone", job: plan.payload.job });
            return;
          }
          break;
      }
    }

    // ── Soft handoff ──
    // Route-based modes navigate to the relevant module BUT do NOT close the
    // loop or exit Maverick. The prediction stays in the queue. The operator
    // can return (Maverick is still mounted) and either explicitly close the
    // loop ("Mark handled") or defer. Navigation alone is not closure.
    setHandoff({
      taskId: current.jobId,
      taskLabel: current.intervention.label,
      jobId: current.jobId,
      customerName: current.customerName,
      actionType: current.failureMode,
      returnRoute: "/maverick",
      reason: current.headline,
      instruction: current.intervention.instruction ?? current.intervention.label,
    });

    void runAction(
      {
        label: current.intervention.label,
        executionMode: "navigate",
        route: plan.fallbackRoute,
      },
      {
        navigate,
        onError: () => toast.error("Couldn't open intervention surface."),
      },
    ).then((handled) => {
      if (handled) {
        toast.info("Maverick is paused here — return and mark handled when done.");
      }
    });
  };

  const handleDefer = () => {
    if (!current) return;
    setClosedJobIds((prev) => new Set(prev).add(current.jobId));
  };

  /** Common success handler for hosted modals — closes the modal, marks the
   *  loop closed, and advances Maverick to the next prediction. */
  const handleModalSuccess = useCallback(() => {
    const jobId = current?.jobId;
    setActiveModal(null);
    if (jobId) advanceAfterClose(jobId);
  }, [current?.jobId, advanceAfterClose]);

  /** Inline Mark Done — clear the selected overdue follow-ups for this job
   *  via setFollowUpTasks, then close the loop. Real state resolution. */
  const handleMarkFollowUpsDone = useCallback(
    (taskIds: string[]) => {
      if (taskIds.length === 0) return;
      const now = new Date().toISOString();
      setFollowUpTasks((prev) =>
        prev.map((t) =>
          taskIds.includes(t.id)
            ? { ...t, status: "completed" as const, completedAt: now }
            : t,
        ),
      );
      toast.success(
        `${taskIds.length} follow-up${taskIds.length === 1 ? "" : "s"} cleared — loop closing.`,
      );
      handleModalSuccess();
    },
    [setFollowUpTasks, handleModalSuccess],
  );

  /** True when the current prediction's intervention is route-based (no
   *  inline modal). Used to swap CTA wording from closure to handoff. */
  const isCurrentRouteBased = useMemo(() => {
    if (!current) return false;
    const plan = resolveMaverickIntervention(current, { jobs, issues });
    return plan.mode === "route";
  }, [current, jobs, issues]);

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* ── Top Bar ── */}
      <div className="h-11 border-b bg-card flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-primary flex items-center justify-center">
            <Zap className="h-3 w-3 text-primary-foreground" />
          </div>
          <h1 className="text-sm font-semibold tracking-tight">Maverick</h1>
          <span className="text-[10px] text-muted-foreground ml-1">Loop Closure</span>
        </div>
        <div className="flex items-center gap-1">
          <MaverickHowItWorksDrawer
            evaluatedCount={result.evaluatedCount}
            suppressedCount={allRemaining.length}
            primaryActiveCount={current ? 1 : 0}
          />
          {isMaverickDebugEnabled() && <MaverickTestPanel />}
          <button
            onClick={disableMaverick}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="h-3 w-3" />
            Exit
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto flex items-start justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          {state === "closed" && closingJobId ? (
            <ClosedConfirmation jobId={closingJobId} />
          ) : current ? (
            <PredictionCard
              prediction={current}
              moreAfter={moreAfter}
              isRouteBased={isCurrentRouteBased}
              onAct={handleAct}
              onCloseLoop={handleCloseLoop}
              onDefer={handleDefer}
              onShowAll={() => setShowAll(true)}
              showAllOpen={showAll}
              allRemaining={allRemaining}
              onSelectFromAll={(jobId) => {
                // Bring selected job to the front of the PRIMARY queue by
                // closing everything ahead of it. Suppressed items can't be
                // selected here (they're disabled in the list).
                const idx = primaryQueue.findIndex((p) => p.jobId === jobId);
                if (idx > 0) {
                  setClosedJobIds((prev) => {
                    const next = new Set(prev);
                    primaryQueue.slice(0, idx).forEach((p) => next.add(p.jobId));
                    return next;
                  });
                }
                setShowAll(false);
              }}
            />
          ) : (
            <WatchingState
              evaluatedCount={result.evaluatedCount}
              suppressedCount={allRemaining.length}
              onShowAll={() => setShowAll(true)}
              showAllOpen={showAll}
              allRemaining={allRemaining}
            />
          )}
        </div>
      </div>

      {/* ── Hosted modals ── */}
      {activeModal?.kind === "ordering" && (
        <OrderingModal
          open
          onOpenChange={(o) => !o && setActiveModal(null)}
          job={activeModal.job}
          openIssues={activeModal.openIssues}
          onSendToOrdering={() => {
            toast.success("Sent to ordering — loop closing.");
            handleModalSuccess();
          }}
        />
      )}
      {activeModal?.kind === "quickAssign" && (
        <QuickAssignModal
          open
          onOpenChange={(o) => !o && setActiveModal(null)}
          job={activeModal.job}
          openIssues={activeModal.openIssues}
          onSuccess={handleModalSuccess}
        />
      )}
      {activeModal?.kind === "quickResolveIssue" && (
        <QuickResolveIssueModal
          open
          onOpenChange={(o) => !o && setActiveModal(null)}
          issue={activeModal.issue}
          onSuccess={handleModalSuccess}
        />
      )}
      {activeModal?.kind === "logInteraction" && (
        <LogInteractionModal
          open
          onOpenChange={(o) => {
            // Cancel/dismiss/ESC must NOT close the loop — only onLogged does.
            if (!o) setActiveModal(null);
          }}
          onLogged={handleModalSuccess}
          customerName={activeModal.customerName}
          relatedJobId={activeModal.relatedJobId}
          defaultNote={activeModal.defaultNote}
        />
      )}
      {activeModal?.kind === "markFollowUpsDone" && (
        <MarkFollowUpsDoneModal
          job={activeModal.job}
          followUpTasks={followUpTasks}
          onCancel={() => setActiveModal(null)}
          onConfirm={handleMarkFollowUpsDone}
        />
      )}
    </div>
  );
}

// ── Watching State ──

function WatchingState({
  evaluatedCount,
  suppressedCount,
  onShowAll,
  showAllOpen,
  allRemaining,
}: {
  evaluatedCount: number;
  suppressedCount: number;
  onShowAll: () => void;
  showAllOpen: boolean;
  allRemaining: PredictedFailure[];
}) {
  return (
    <div className="text-center py-16">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted/40 mb-5">
        <Eye className="h-5 w-5 text-muted-foreground" />
      </div>
      <h2 className="text-xl font-semibold tracking-tight">Watching</h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
        Nothing actionable predicted to fail in the next 7 days. Monitoring{" "}
        <span className="font-medium text-foreground">{evaluatedCount} active jobs</span> across
        materials, permits, comms, financial, follow-ups, crew, and issue signals.
      </p>
      <p className="text-[11px] text-muted-foreground/80 mt-3 max-w-sm mx-auto">
        Open <span className="font-medium">How Maverick works</span> in the top bar to see exactly
        what would trigger an action.
      </p>
      {suppressedCount > 0 && (
        <div className="mt-6">
          {showAllOpen ? (
            <div className="max-w-md mx-auto text-left">
              <AllPredictionsList items={allRemaining} onSelect={() => {}} />
            </div>
          ) : (
            <button
              onClick={onShowAll}
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              {suppressedCount} trend signal{suppressedCount === 1 ? "" : "s"} below action
              threshold ·{" "}
              <span className="underline underline-offset-2">Show all</span>
            </button>
          )}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground/60 mt-6 uppercase tracking-wider">
        Loop closure engine · idle
      </p>
    </div>
  );
}

// ── Closed Confirmation ──

function ClosedConfirmation({ jobId }: { jobId: string }) {
  return (
    <div className="text-center py-20 animate-in fade-in duration-300">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 mb-5">
        <CheckCircle2 className="h-6 w-6 text-primary" />
      </div>
      <h2 className="text-xl font-semibold tracking-tight">Loop closed</h2>
      <p className="text-sm text-muted-foreground mt-2">
        Watching <span className="font-mono text-foreground">{jobId}</span> for the next signal.
      </p>
    </div>
  );
}

// ── Prediction Card ──

function PredictionCard({
  prediction,
  moreAfter,
  isRouteBased,
  onAct,
  onCloseLoop,
  onDefer,
  onShowAll,
  showAllOpen,
  allRemaining,
  onSelectFromAll,
}: {
  prediction: PredictedFailure;
  moreAfter: number;
  isRouteBased: boolean;
  onAct: () => void;
  onCloseLoop: () => void;
  onDefer: () => void;
  onShowAll: () => void;
  showAllOpen: boolean;
  allRemaining: PredictedFailure[];
  onSelectFromAll: (jobId: string) => void;
}) {
  const { headline, customerName, confidence, evidence, intervention } = prediction;
  const moduleLabel = routeToModuleLabel(intervention.route);

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Confidence badge */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-destructive/10 text-destructive">
          <span className="h-1.5 w-1.5 rounded-full bg-destructive animate-pulse" />
          Predicted failure
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums">
          {confidence}% confidence
        </span>
      </div>

      {/* Headline */}
      <div>
        <h2 className="text-2xl font-semibold tracking-tight leading-snug">{headline}</h2>
        <p className="text-sm text-muted-foreground mt-1.5">{customerName}</p>
      </div>

      {/* Evidence */}
      <div className="border-t border-border pt-5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Why I'm flagging this
        </p>
        <ul className="space-y-2">
          {evidence.map((sig) => (
            <li key={sig.key} className="flex items-start gap-2 text-sm">
              <span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground/60 shrink-0" />
              <span className="text-foreground/85">{sig.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Intervention */}
      <div className="border border-border rounded-lg p-4 bg-card">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          {isRouteBased ? "Where to handle this" : "What closes the loop"}
        </p>
        <p className="text-sm font-medium text-foreground mb-2">{intervention.label}</p>
        {intervention.draft && (
          <div className="rounded-md bg-muted/40 px-3 py-2.5 text-sm text-foreground/80 italic mb-4 border border-border/50">
            "{intervention.draft}"
          </div>
        )}
        {intervention.instruction && !intervention.draft && (
          <p className="text-sm text-muted-foreground mb-4">{intervention.instruction}</p>
        )}
        <div className="flex items-center gap-2">
          {isRouteBased ? (
            <>
              <Button size="sm" onClick={onAct} className="gap-1.5">
                <ExternalLink className="h-3.5 w-3.5" />
                Open in {moduleLabel}
              </Button>
              <Button size="sm" variant="ghost" onClick={onCloseLoop} className="gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Mark handled
              </Button>
            </>
          ) : intervention.draft ? (
            <>
              <Button size="sm" onClick={onAct} className="gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Send & Close Loop
              </Button>
              <Button size="sm" variant="ghost" onClick={onCloseLoop} className="gap-1.5">
                <Edit3 className="h-3.5 w-3.5" />
                Mark sent
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={onAct} className="gap-1.5">
              {intervention.label}
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onDefer} className="gap-1.5 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            Defer
          </Button>
        </div>
        {isRouteBased && (
          <p className="text-[10px] text-muted-foreground/70 mt-3">
            Opening {moduleLabel} doesn't close the loop. Return and mark handled when done.
          </p>
        )}
      </div>

      {/* Quiet escape hatch */}
      {moreAfter > 0 && (
        <div className="pt-2">
          {showAllOpen ? (
            <AllPredictionsList items={allRemaining} onSelect={onSelectFromAll} />
          ) : (
            <button
              onClick={onShowAll}
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              {moreAfter} more predicted {moreAfter === 1 ? "failure" : "failures"} after this ·{" "}
              <span className="underline underline-offset-2">Show all</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AllPredictionsList({
  items,
  onSelect,
}: {
  items: PredictedFailure[];
  onSelect: (jobId: string) => void;
}) {
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-3 py-2 bg-muted/30 border-b border-border">
        All predicted failures
      </p>
      <ul>
        {items.map((p, i) => {
          const suppressed = !!p.suppressedFromPrimary;
          const isCurrent = i === 0 && !suppressed;
          return (
            <li key={p.jobId}>
              <button
                onClick={() => !suppressed && onSelect(p.jobId)}
                disabled={isCurrent || suppressed}
                title={suppressed ? "Trend signal — not actionable as a single click" : undefined}
                className={cn(
                  "w-full text-left px-3 py-2.5 flex items-center justify-between gap-3 text-xs border-b border-border last:border-0 transition-colors",
                  isCurrent
                    ? "bg-accent/40 cursor-default"
                    : suppressed
                      ? "bg-muted/20 cursor-default opacity-70"
                      : "hover:bg-muted/40",
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{p.headline}</p>
                  <p className="text-muted-foreground truncate text-[10px] mt-0.5">
                    {p.customerName}
                    {suppressed && (
                      <span className="ml-1.5 uppercase tracking-wider">· trend signal</span>
                    )}
                  </p>
                </div>
                <span className="text-[10px] tabular-nums text-muted-foreground shrink-0">
                  {p.confidence}%
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Mark Follow-ups Done Modal (inline closure for followup_dropping_ball) ──

function MarkFollowUpsDoneModal({
  job,
  followUpTasks,
  onCancel,
  onConfirm,
}: {
  job: Job;
  followUpTasks: FollowUpTask[];
  onCancel: () => void;
  onConfirm: (taskIds: string[]) => void;
}) {
  const overdue = useMemo(
    () =>
      followUpTasks.filter(
        (t) =>
          t.jobId === job.id &&
          t.status !== "completed" &&
          t.dueDate &&
          new Date(t.dueDate).getTime() < Date.now(),
      ),
    [followUpTasks, job.id],
  );

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(overdue.map((t) => t.id)),
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Clear overdue follow-ups</DialogTitle>
          <DialogDescription>
            Mark these follow-ups complete for {job.customerName}. The loop closes when real
            state is updated — not on a logged note.
          </DialogDescription>
        </DialogHeader>

        {overdue.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No overdue follow-ups found for this job. The signal may have already cleared.
          </p>
        ) : (
          <ul className="space-y-2 max-h-72 overflow-y-auto py-2">
            {overdue.map((t) => {
              const due = t.dueDate ? new Date(t.dueDate) : null;
              const hoursOverdue = due
                ? Math.floor((Date.now() - due.getTime()) / 36e5)
                : 0;
              return (
                <li
                  key={t.id}
                  className="flex items-start gap-3 p-3 rounded-md border border-border bg-card hover:bg-muted/30 transition-colors"
                >
                  <Checkbox
                    id={`fu-${t.id}`}
                    checked={selected.has(t.id)}
                    onCheckedChange={() => toggle(t.id)}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor={`fu-${t.id}`}
                    className="flex-1 cursor-pointer min-w-0"
                  >
                    <p className="text-sm font-medium text-foreground">
                      {t.type ?? "Follow-up"}
                      {t.dispositionReason && (
                        <span className="text-muted-foreground font-normal">
                          {" · "}
                          {t.dispositionReason}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {hoursOverdue >= 24
                        ? `${Math.floor(hoursOverdue / 24)}d overdue`
                        : `${hoursOverdue}h overdue`}
                      {t.assignedToLabel && ` · ${t.assignedToLabel}`}
                    </p>
                    {t.notes && (
                      <p className="text-[11px] text-foreground/70 mt-1 line-clamp-2">
                        {t.notes}
                      </p>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={selected.size === 0}
            onClick={() => onConfirm(Array.from(selected))}
            className="gap-1.5"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Mark {selected.size} done & close loop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
